import { getIstComponents } from './marketHours';

/**
 * Official Diwali Dates (Laxmi Pujan / Deepavali main festival dates)
 * Source: NSE India Market Holiday Schedule & Hindu Panchang (Kartik Amavasya)
 */
export const DIWALI_DATES: Record<number, string> = {
  2023: '2023-11-12',
  2024: '2024-11-01',
  2025: '2025-10-20',
  2026: '2026-11-08',
  2027: '2027-10-29',
  2028: '2028-10-17',
  2029: '2029-11-05',
  2030: '2030-10-26',
};

export const DIWALI_THEME_STORAGE_KEY = 'niftysutra-diwali-theme';

export interface DiwaliSeasonInfo {
  isSeason: boolean;
  diwaliDateStr: string | null;
  startDateStr: string | null;
  endDateStr: string | null;
  daysUntilDiwali: number | null;
  stage: 'before' | 'today' | 'after' | 'none';
  formattedGreeting: string;
}

/**
 * Checks if a given date (default: now in IST) falls within the Diwali festive window:
 * from 10 days before the main Diwali date till 5 days later (inclusive).
 */
export function getDiwaliSeasonInfo(date: Date = new Date()): DiwaliSeasonInfo {
  const { year, isoDate } = getIstComponents(date);

  // Check current year, and adjacent years (in case boundary wraps around year end)
  const candidateYears = [year, year - 1, year + 1];

  for (const y of candidateYears) {
    const diwaliStr = DIWALI_DATES[y];
    if (!diwaliStr) continue;

    const [dYear, dMonth, dDay] = diwaliStr.split('-').map(Number);
    const diwaliDate = new Date(Date.UTC(dYear, dMonth - 1, dDay));

    const startDate = new Date(diwaliDate);
    startDate.setUTCDate(startDate.getUTCDate() - 10);

    const endDate = new Date(diwaliDate);
    endDate.setUTCDate(endDate.getUTCDate() + 5);

    const startStr = startDate.toISOString().slice(0, 10);
    const endStr = endDate.toISOString().slice(0, 10);

    if (isoDate >= startStr && isoDate <= endStr) {
      const msDiff = new Date(isoDate).getTime() - new Date(diwaliStr).getTime();
      const dayDiff = Math.round(msDiff / (1000 * 60 * 60 * 24));

      let stage: 'before' | 'today' | 'after' = 'before';
      let formattedGreeting = 'Shubh Deepawali';

      if (dayDiff === 0) {
        stage = 'today';
        formattedGreeting = 'Happy Diwali & Muhurat Trading!';
      } else if (dayDiff > 0) {
        stage = 'after';
        formattedGreeting = 'Saal Mubarak & Happy Diwali';
      } else if (dayDiff === -1) {
        formattedGreeting = 'Choti Diwali & Dhanteras';
      } else if (dayDiff >= -3 && dayDiff <= -2) {
        formattedGreeting = 'Happy Dhanteras & Diwali';
      } else {
        formattedGreeting = `Diwali in ${Math.abs(dayDiff)} days`;
      }

      return {
        isSeason: true,
        diwaliDateStr: diwaliStr,
        startDateStr: startStr,
        endDateStr: endStr,
        daysUntilDiwali: dayDiff === 0 ? 0 : -dayDiff,
        stage,
        formattedGreeting,
      };
    }
  }

  return {
    isSeason: false,
    diwaliDateStr: null,
    startDateStr: null,
    endDateStr: null,
    daysUntilDiwali: null,
    stage: 'none',
    formattedGreeting: 'Shubh Deepawali',
  };
}

/**
 * Returns whether the Diwali theme should be active on startup:
 * 1. If user explicitly stored 'enabled', returns true.
 * 2. If user explicitly stored 'disabled', returns false.
 * 3. Default (null / 'auto'): Automatically true if current date is within the 10-days-before to 5-days-after window.
 */
export function getInitialDiwaliThemeState(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const stored = localStorage.getItem(DIWALI_THEME_STORAGE_KEY);
    if (stored === 'enabled') return true;
    if (stored === 'disabled') return false;
  } catch {
    // Ignore localStorage access failures
  }

  // Automatic schedule: 10 days before till 5 days after Diwali
  return getDiwaliSeasonInfo().isSeason;
}
